import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { Dropdown, Radio, Text, Input, Icon } from "tea-component";
// import InfoBubble from '@gp/components/InfoBubble';
import classNames from 'classnames';
import './index.scss';

interface OPT_TYPES {
  options: {value: string, text: any, disabled?: boolean}[],
  defaultValue?: string,
  placeholder: string,
  clearable?: boolean,
  isDisabledSelected?: boolean, // 禁用时是否选中对应项
  isDisabledHide?: boolean, // 禁用时是否隐藏对应项
  custombuttonable?: boolean,
  customInputPlaceholder?: string | undefined,
  customRepeatCheck?: boolean, // 自定义内容时是否开启重复检查，confirm时校验，此时previousList必传
  customInputIsRepeatBubbleContent?: any; // 重复时气泡提示内容
  isNoOptionsJustCustom?: boolean; // options没有内容时是否只显示自定义输入
}

interface DropDownRadioSelectPropsType {
  className?: string;
  style?: React.CSSProperties;
  isFont14?: boolean;
  allOpt: OPT_TYPES;
  onChange: ((v: string | undefined, message: {isRepeat: boolean}) => void) | null;
  previousList?: string[] | null; // 已经选择的内容
}

const DEFAULT_OPT: OPT_TYPES = {
  options: [],
  defaultValue: undefined,
  placeholder: '',
  clearable: true,
  isDisabledSelected: false,
  isDisabledHide: false,
  custombuttonable: false,
  customInputPlaceholder: '',
  customRepeatCheck: false,
  customInputIsRepeatBubbleContent: undefined,
  isNoOptionsJustCustom: true,
};

const DEFAULT_PROPS: DropDownRadioSelectPropsType = {
  className: '',
  style: {},
  isFont14: false,
  allOpt: DEFAULT_OPT,
  onChange: null,
  previousList: null,
};

function DropDownRadioSelect(props: DropDownRadioSelectPropsType) {

  const {
    className,
    style,
    isFont14,
    allOpt,
    onChange,
    previousList,
  } = props;

  const [options, setOptions] = useState<{value: string, text: any, disabled?: boolean}[]>([]);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [repeatRender, setRepeatRender] = useState<boolean>(false);
  const [isHandleChange, setIsHandleChange] = useState<boolean>(false); // value值是否触发onChange
  const [lastValue, setLastValue] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>('');
  // 控制下拉框显示/隐藏
  const [open, setOpen] = useState<boolean>(false);
  // 控制自定义按钮的显示/隐藏，也起着控制自定义输入的展示的作用
  const [isShowCustomBtn, setIsShowCustomBtn] = useState<boolean>(true);
  // 自定义输入的内容
  const [inputValue, setInputValue] = useState<string>('');
  const [clearBtnVisible, setClearBtnVisible] = useState<boolean>(false);
  const [isRepeat, setIsRepeat] = useState<boolean | null>(null);

  const newOpt: OPT_TYPES = useMemo(() => {
    return allOpt ? ({...DEFAULT_OPT, ...allOpt}) : DEFAULT_OPT;
  }, [allOpt]);

  const isNoOptions: boolean = useMemo(() => {
    const isNoOpt: boolean = !options || !options?.length; // options是否有数据
    // 当options有数据但全部是禁用的，且禁用项不展示时
    const isAllDisabledAndHide: boolean = isNoOpt ? true : (options?.every((t: any) => t?.disabled) && newOpt?.isDisabledHide as boolean);
    const res = isNoOpt || isAllDisabledAndHide;
    return res;
  }, [options, newOpt?.isDisabledHide]);

  const isJustCustom: boolean = useMemo(() => {
    return isNoOptions && newOpt?.isNoOptionsJustCustom as boolean;
  }, [isNoOptions, newOpt?.isNoOptionsJustCustom]);

  const getIsRepeat = (v: string): boolean | null => {
    // 是否有重复选择
    let isRepeat: boolean | null = null, realVal = v;
    // 检查是否有重复
    if (newOpt?.customRepeatCheck && Array.isArray(previousList)) {
      // 先看是否是当前options中的
      for (let i = 0; i < options?.length; i ++) {
        const t = options?.[i];
        if (t?.text === v) {
          realVal = t?.value;
        }
      }

      for (let i = 0; i < previousList?.length; i ++) {
        const t = previousList?.[i];
        if ((typeof realVal === 'string') && (typeof t === 'string') && (realVal?.trim() === t?.trim())) {
          isRepeat = true;
        }
        if ((i === (previousList?.length - 1)) && (isRepeat === null)) {
          isRepeat = false;
        }
      }

      isHandleChange && onChange?.(
        realVal,
        {
          isRepeat: isRepeat as boolean,
        },
      );
    }

    return isRepeat;
  }

  useEffect(() => {
    if (newOpt) {
      setOptions(newOpt?.options || []);
    }
  }, [newOpt?.options]);

  // 默认值
  useEffect(() => {
    if (newOpt?.defaultValue) {
      setValue(newOpt?.defaultValue);
      setLastValue(newOpt?.defaultValue);
    }
  }, [newOpt?.defaultValue]);

  // value值发生改变时触发onChange，改变button文案显示
  useEffect(() => {
    if (isRepeat || repeatRender) {
      setRepeatRender(false);
      return;
    }
    let newButtonText = '', realVal = value;
    if (newOpt?.custombuttonable && !isShowCustomBtn) { // 自定义内容
      for (let i = 0; i < options?.length; i ++) {
        const t = options?.[i];
        if (t?.text === value) {
          newButtonText = t?.text;
          realVal = t?.value;
          setValue(t?.value);
          setLastValue(t?.value);
          setRepeatRender(true); // 避免重复执行当前的effect
        }
      }
      // 默认值显示
      if (!newButtonText) {
        newButtonText = value || '';
      }
    } else { // 常规选择内容
      for (let i = 0; i < options?.length; i ++) {
        const t = options?.[i];
        if (t?.value === value) {
          newButtonText = t?.text;
        }
      }
      // 默认值显示
      if (!newButtonText) {
        newButtonText = value || '';
      }
    }
    setButtonText(newButtonText);
    (isRepeat === null) && isHandleChange && onChange?.(realVal, {isRepeat: false});
  }, [value]);

  // 点击清空按钮
  const HandleClear = useCallback(() => {
    setValue(undefined);
    setLastValue(undefined);
    setIsHandleChange(true);
  }, []);

  return (
    <Dropdown
      className={classNames({
        'gp-drop-down-radio-select-content': true,
        [className as string]: !!className,
        'font14': isFont14,
        'placeholder-color': !value,
      })}
      style={style}
      button={<section className="button-box" title={buttonText}>{buttonText || newOpt?.placeholder}</section>}
      appearance="button"
      matchButtonWidth
      clickClose={false}
      onClose={() => {setIsRepeat(null); setTimeout(() => setIsShowCustomBtn(true), 300);}}
      clearable={!!value && newOpt?.clearable}
      onClear={HandleClear}
      open={open}
      onOpenChange={(o: boolean) => setOpen(o)}
      boxClassName={classNames({
        'gp-drop-down-radio-selectdrop-down-list': true,
        'globalpedia-modal-font14': isFont14,
        'hide-options-list': !isShowCustomBtn,
      })}
    >
      {close => (
        <>
          {isJustCustom || (
            <>
              <Radio.Group
                value={value}
                onChange={(v: string) => {
                  setIsHandleChange(true);
                  if (v == lastValue) {
                    setValue(undefined);
                    setLastValue(undefined);
                  } else {
                    setValue(v);
                    setLastValue(v);
                  }
                  close();
                }}
                layout="column"
              >
                {options?.map((t: {value: string, text: any, disabled?: boolean}) => {
                  const isDisabledSelected = t?.disabled && newOpt?.isDisabledSelected;
                  const isHide = t?.disabled && newOpt?.isDisabledHide;
                  return isHide ? null : (
                    <Radio
                      key={t?.value}
                      className={isDisabledSelected ? 'is-disabled-selected' : ''}
                      name={t?.value}
                      disabled={t?.disabled || false}
                      value={isDisabledSelected ? true : undefined}
                    >
                      {t?.text || '-'}
                    </Radio>
                  );
                })}
              </Radio.Group>
              <>
                {newOpt?.custombuttonable && isShowCustomBtn && (
                  <section className="custom-btn-box">
                    <Text
                      theme="primary"
                      className="custom-btn"
                      onClick={() => setIsShowCustomBtn(false)}
                    >
                      Customize
                    </Text>
                  </section>
                )}
              </>
            </>
          )}
          {(isJustCustom || (newOpt?.custombuttonable && !isShowCustomBtn)) && (
            <section className="custom-body">
              {/* <InfoBubble
                className={classNames({
                  'gp-drop-down-radio-select-custom-body-bubble': true,
                  "globalpedia-modal-font14": isFont14,
                })}
                theme="white"
                placement="right"
                visible={isRepeat || undefined}
                customBubbleContent={isRepeat ? newOpt?.customInputIsRepeatBubbleContent : undefined}
                DomOrText={}
              /> */}
              <article className={`custom-input-box ${isRepeat ? 'is-error' : ''}`}>
                <Input
                  className="custom-input"
                  size="full"
                  placeholder={newOpt?.customInputPlaceholder}
                  value={inputValue}
                  onChange={(v: string) => {
                    setIsRepeat(false);
                    setInputValue(v);
                    setClearBtnVisible(!!v);
                  }}
                />
                {clearBtnVisible && (
                  <Icon
                    type="dismiss"
                    style={{
                      cursor: 'pointer',
                      marginRight: 10,
                    }}
                    onClick={() => {
                      setIsRepeat(false);
                      setInputValue('');
                      setClearBtnVisible(false);
                    }}
                  />
                )}
              </article>
              {isRepeat && (
                <label className="is-error-info">{newOpt?.customInputIsRepeatBubbleContent}</label>
              )}
              <aside className="custom-handle-btn-box" style={{textAlign: 'right'}}>
                <Text
                  className={classNames({
                    "confirm-btn": true,
                    "confirm-usable": inputValue?.trim(),
                    "confirm-disabled": !inputValue?.trim(),
                  })}
                  onClick={() => {
                    const v = inputValue?.trim();
                    if (v) {
                      setIsHandleChange(true);
                      // 是否有重复选择
                      const isRepeat: boolean | null = getIsRepeat(v);
                      setIsRepeat(isRepeat);
                      if (isRepeat) return;
                      setOpen(false);
                      setTimeout(() => setIsShowCustomBtn(true), 300);
                      setValue(v);
                      setLastValue(undefined);
                      close();
                    }
                  }}
                >
                  Confirm
                </Text>
                {isJustCustom || (
                  <Text
                    theme="primary"
                    className="back-btn"
                    onClick={() => {setIsShowCustomBtn(true); setIsRepeat(null);}}
                  >
                    Back
                  </Text>
                )}
              </aside>
            </section>
          )}
        </>
      )}
    </Dropdown>
  )
}

DropDownRadioSelect.defaultProps = DEFAULT_PROPS;

export default memo(DropDownRadioSelect);
